import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Space, Divider, Spin, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedRole, updateCurrentRole } from "../store/auth";
import axios from "axios";
import {
  UserSwitchOutlined,
  RightOutlined,
  DashboardOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  UserOutlined,
  BankOutlined,
  LoadingOutlined,
  CheckCircleFilled
} from "@ant-design/icons";

const { Title, Text } = Typography;

const RoleSelectionModal = () => {
  const { user, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedRoleData, setSelectedRoleData] = useState(null);

  useEffect(() => {
    if (!user || !user.idToken) {
      setOpen(false);
      return;
    }

    // Identify all true roles for the user
    const roles = [];
    if (user.is_supervisor) roles.push({ key: "supervisor", label: "Supervisor", icon: <DashboardOutlined /> });
    if (user.is_tl) roles.push({ key: "team_lead", label: "Team Lead", icon: <TeamOutlined /> });
    if (user.is_tc) roles.push({ key: "telecaller", label: "Telecaller", icon: <CustomerServiceOutlined /> });
    if (user.is_fa) roles.push({ key: "field_agent", label: "Field Agent", icon: <UserOutlined /> });
    if (user.is_agency) roles.push({ key: "agency", label: "Agency", icon: <BankOutlined /> });

    // If user has no selected role yet...
    if (!user.selectedRole) {
      if (roles.length > 1) {
        // Multiple roles available -> Force selection
        setAvailableRoles(roles);
        setOpen(true);
      } else if (roles.length === 1) {
        // Only one role available -> Auto select (background)
        dispatch(updateCurrentRole(roles[0].key)).unwrap().then(() => {
          if (roles[0].key === 'telecaller') {
            axios.post('auth/attendance/action/', {}, { headers: { Authorization: `Bearer ${token}` } })
              .catch(err => console.error('Attendance Auto Hit Error:', err));
          }
          dispatch(setSelectedRole(roles[0].key));
        }).catch(() => {
          // Fallback even if api fails
          dispatch(setSelectedRole(roles[0].key));
        });
      }
    } else {
      // Role already selected
      setOpen(false);
    }
  }, [user, dispatch]);

  const handleRoleSelect = async (role) => {
    setIsRedirecting(true);
    setSelectedRoleData(role);
    try {
      await dispatch(updateCurrentRole(role.key)).unwrap();

      if (role.key === 'telecaller') {
        axios.post('auth/attendance/action/', {}, { headers: { Authorization: `Bearer ${token}` } })
          .catch(err => console.error('Attendance Manual Hit Error:', err));
      }

      // Artificial short delay for smooth UX transition
      setTimeout(() => {
        dispatch(setSelectedRole(role.key));
        setOpen(false);
        setIsRedirecting(false);
      }, 600);

    } catch (error) {
      message.error("Failed to set workspace. Please try again.");
      setIsRedirecting(false);
      setSelectedRoleData(null);
    }
  };

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={480}
      bodyStyle={{ padding: "32px" }}
      className="role-selection-modal"
    >
      {!isRedirecting ? (
        <>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div style={{
              width: "64px",
              height: "64px",
              background: "#e6f4ff",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px"
            }}>
              <UserSwitchOutlined style={{ fontSize: "32px", color: "#1677ff" }} />
            </div>
            <Title level={3} style={{ marginTop: 0, marginBottom: "8px", fontWeight: 600 }}>
              Select Workspace
            </Title>
            <Text type="secondary" style={{ fontSize: "15px" }}>
              Please select the role workspace you want to access for this session.
            </Text>
          </div>

          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {availableRoles.map((role) => (
              <div
                key={role.key}
                onClick={() => handleRoleSelect(role)}
                style={{
                  padding: "16px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: "12px",
                  border: "1px solid #e8e8e8",
                  background: "#ffffff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#1677ff";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(22,119,255,0.1)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8e8e8";
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.02)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <Space size="middle" align="center">
                  <div style={{
                    color: "#1677ff",
                    fontSize: "20px",
                    display: "flex",
                    background: "#f5f5f5",
                    padding: "8px",
                    borderRadius: "8px"
                  }}>
                    {role.icon}
                  </div>
                  <Text style={{ fontWeight: 500, fontSize: "16px" }}>{role.label}</Text>
                </Space>
                <RightOutlined style={{ color: "#bfbfbf", fontSize: "14px" }} />
              </div>
            ))}
          </Space>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "#1677ff" }} spin />} />
          <Title level={4} style={{ marginTop: "24px", marginBottom: "8px" }}>
            Redirecting...
          </Title>
          <Text type="secondary" style={{ fontSize: "15px" }}>
            Preparing your <strong style={{ color: "#1677ff" }}>{selectedRoleData?.label}</strong> workspace
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default RoleSelectionModal;
